import {
  IDetailCellRendererParams,
  IServerSideGetRowsParams
} from 'ag-grid-community';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { ComposedTable } from 'components';
import { useClient } from 'components/ClientProvider';
import { CustomLoadingOverlay } from 'components/CustomLoadingOverlay';
import {
  AP_SCHEDULE_BLOCK_LOAD_DEBOUNCE,
  AP_SCHEDULE_GRID_INITIAL_ROW_COUNT,
  AP_SCHEDULE_MAX_PAGES_IN_CACHE,
  AP_SCHEDULE_MIN_SEARCH_LENGTH,
  AP_SCHEDULE_PAGE_SIZE,
  DEFAULT_COL_WIDTH,
  MIN_COL_WIDTH
} from './constants';
import { labels } from './messages';
import { StyledTableHeader } from './styled';
import {
  ApScheduleInvoice,
  ApScheduleResponse,
  ApScheduleVendor
} from './types';
import { useApScheduleColumnDefs } from './useApScheduleColumnDefs';

export const ApScheduleTable = (): JSX.Element => {
  const client = useClient();
  const intl = useIntl();
  const [searchInputValue, setSearchInputValue] = useState('');
  const [debouncedSearchInputValue, setDebouncedSearchInputValue] =
    useState('');
  const onDebouncedSearchInputChange = useCallback((searchValue: string) => {
    setDebouncedSearchInputValue(
      searchValue.length >= AP_SCHEDULE_MIN_SEARCH_LENGTH ? searchValue : ''
    );
  }, []);
  const { masterColDefs, detailColDefs } = useApScheduleColumnDefs();
  const serverSideDatasource = useMemo(
    () => ({
      getRows: async ({
        success
      }: IServerSideGetRowsParams<ApScheduleResponse>) => {
        success({
          rowData: [
            {
              vendorNo: '123',
              vendorName: 'Test Vendor',
              paymentMethod: 'Check',
              amount: '$123.45',
              discountAmount: '$23.45',
              netAmount: '$100.00'
            }
          ]
        });
      }
    }),
    []
  );
  const detailCellRendererParams = useMemo(
    () =>
      ({
        detailGridOptions: {
          columnDefs: detailColDefs
        },
        getDetailRowData: params => {
          params.successCallback([
            {
              vendorId: '123',
              invoiceNumber: '123-456',
              poNumber: '1234',
              invoiceDate: '2025-03-19',
              dueDate: '2025-04-19',
              invoiceAmount: '$123.45',
              discountAmount: '$23.45',
              netAmount: '$100.00',
              balanceForwardAmount: '$50.00',
              currentMonthAmount: '$1,245.56',
              currentToDateAmount: '$3,132.01',
              postAheadAmount: '$874.12',
              checkNumber: 'CHK001',
              checkAmount: '$123.45',
              checkDate: '2025-03-19',
              hold: false,
              status: 'To Be Paid',
              comment: 'Test comment'
            }
          ]);
        }
      } as IDetailCellRendererParams<ApScheduleVendor, ApScheduleInvoice>),
    [detailColDefs]
  );
  console.log(debouncedSearchInputValue);
  return (
    <ComposedTable<ApScheduleVendor, ApScheduleVendor>
      columnDefs={masterColDefs}
      customHeaderCount={0}
      defaultColDef={{
        width: DEFAULT_COL_WIDTH,
        minWidth: MIN_COL_WIDTH
      }}
      downloadDialogTitle={intl.formatMessage(labels.needsAuthorizationTitle)}
      customHeight={client?.isMFE ? 'calc(100vh - 230px)' : undefined}
      detailCellRendererParams={detailCellRendererParams}
      exportData={undefined}
      getCSVDocument={undefined}
      getExcelDocument={undefined}
      getPDFDocument={undefined}
      getRowId={param => param.data?.vendorNo}
      // invoiceCount={data?.totalNoOfInvoices}
      isDownloadable
      // isExportDataFetching={isExportDataFetching}
      isPrintable
      isSearchable
      loadingOverlayComponent={CustomLoadingOverlay}
      rowModelType="serverSide"
      serverSideDatasource={serverSideDatasource}
      cacheBlockSize={AP_SCHEDULE_PAGE_SIZE}
      masterDetail
      maxBlocksInCache={AP_SCHEDULE_MAX_PAGES_IN_CACHE}
      blockLoadDebounceMillis={AP_SCHEDULE_BLOCK_LOAD_DEBOUNCE}
      onDebouncedSearchInputChange={onDebouncedSearchInputChange}
      onSearchInputChange={setSearchInputValue}
      purgeClosedRowNodes={false}
      reactiveCustomComponents
      serverSidePivotResultFieldSeparator="_"
      serverSideOnlyRefreshFilteredGroups={false}
      serverSideInitialRowCount={AP_SCHEDULE_GRID_INITIAL_ROW_COUNT}
      scrollToTopButtonShow={false}
      searchInputValue={searchInputValue}
      // setIsExportDataQueryEnabled={setIsExportDataQueryEnabled}
      suppressAggFuncInHeader
      suppressMultiSort
      suppressRowClickSelection
      suppressRowDeselection
      // suppressServerSideFullWidthLoadingRow={isFilterChanged}
      tableHeaderTitle={intl.formatMessage(labels.tableTitle)}
      tableHeaderContent={
        <StyledTableHeader>{/* Filters will go here */}</StyledTableHeader>
      }
      // tableHeaderFilters={{
      //   selectedStore,
      //   selectedDueDate,
      //   selectedPaymentMethods
      // }}
      // totalsInFooter={{
      //   selectedTotalInFooter,
      //   discountTotalInFooter,
      //   totalOwedInFooter
      // }}
      useCustomSearchFilter={false}
    />
  );
};
